import { Box, Button, Checkbox, FormField, Input, InputArea, LinearProgressBar, RadioGroup } from '@wix/design-system'
import { useContent, useMetadata } from '@wix/funnel-builder-lizard'
import { emailRegex } from '@wix/wix-js-validations'
import React, { FC, Fragment, useEffect, useRef, useState } from 'react'

import { useViewport } from '../../../hooks/useViewport'
import { PhoneNumberControl } from '../../reusable/PhoneNumberControl'
import { CountryCode } from '../../reusable/PhoneNumberControl/types'
import { Content, Screen } from '.'
import s from './EnterpriseFormRenderer.scss'
import { urlRegex } from './patterns'

type Validations = Record<string, string | undefined>

const PRIVACY_URL = 'https://www.wix.com/about/privacy'

const localeBasedFieldsReorder = (fields: Screen['fields']['items'], language: string) => {
  const firstNameField = fields.find(f => f.key === 'first_name')
  const lastNameField = fields.find(f => f.key === 'last_name')
  const otherFields = fields.filter(f => f.key !== 'first_name' && f.key !== 'last_name')

  // `ja` should have `first_name` and `last_name` swapped
  if (language === 'ja' && firstNameField && lastNameField) {
    return [lastNameField, firstNameField, ...otherFields]
  }

  return fields
}

export const EnterpriseFormRenderer: FC<{
  screen: Screen
  onNext: any
  answers: any
  setAnswers: (setter: (args: any) => void) => void
  step: number
  stepsCount: number
}> = ({ step, stepsCount, screen, onNext, answers, setAnswers }) => {
  const [{ language }] = useMetadata()
  const { viewport } = useViewport()
  const [validations, setValidations] = useState<Validations>({})
  const [globalError, setGlobalError] = useState<string>('')
  const [agreementSelected, setAgreementSelected] = useState<boolean>(false)
  const { content } = useContent<Content>()
  const fields = localeBasedFieldsReorder(screen.fields.items, language)
  const isCheckboxes = fields.every(i => i.type === 'checkbox')
  const isRadiogroup = fields.every(i => i.type === 'radio')
  const selectedRadiogroupValue = isRadiogroup && fields.map(i => i.key).find(k => answers[k])
  const selectedCheckboxValue = isCheckboxes && fields.map(i => i.key).find(k => answers[k])

  const onSubmit = () => {
    const _validations: Validations = {}

    if (isCheckboxes && !selectedCheckboxValue) {
      setGlobalError(content.checkboxesValidationError)
      return
    }

    fields.forEach(field => {
      const answer = answers[field.key]

      if (field.type === 'phone') {
        const str = `${answer?.[0]?.dialCode}${answer?.[1]}`

        if (!answer || !answer?.[1]) {
          _validations[field.key] = content.emptyValidationError
        }

        if (!window.intlTelInputUtils?.isValidNumber(str)) {
          _validations[field.key] =
            viewport === 'mobile' ? content.phoneValidationErrorMobile : content.phoneValidationError
        }
      }

      if (field.type === 'url' && !answer?.match(urlRegex)) {
        _validations[field.key] = viewport === 'mobile' ? content.urlValidationErrorMobile : content.urlValidationError
      }

      if (field.type === 'email' && !answer?.match(emailRegex)) {
        _validations[field.key] =
          viewport === 'mobile' ? content.emailValidationErrorMobile : content.emailValidationError
      }

      if (['input', 'url', 'email', 'textarea'].includes(field.type) && !answer) {
        _validations[field.key] = content.emptyValidationError
      }
    })

    setValidations(_validations)

    if (screen.agreement && !agreementSelected) {
      setGlobalError(content.agreementValidationError)
      return
    }

    if (Object.values(_validations).filter(a => a).length === 0) {
      onNext()
    }
  }

  const onInputChange = (k: string, value: string) => {
    if (validations[k] !== undefined) {
      setValidations(validations => ({ ...validations, [k]: undefined }))
    }
    setAnswers((old: any) => ({ ...old, [k]: value }))
  }

  // these to handle auto-next for radiobuttons
  const radioRef = useRef(selectedRadiogroupValue)
  useEffect(() => {
    if (radioRef.current !== selectedRadiogroupValue) {
      onNext()
    }
  }, [onNext, selectedRadiogroupValue])

  const onRadioClick = (k: any) => {
    const optionKeys = screen.fields.items
      .map(i => i.key)
      .reduce((acc, i) => {
        acc[i] = false
        return acc
      }, {} as Record<string, boolean>)

    const newAnswers = { ...answers, ...optionKeys, [k]: true }
    setAnswers(newAnswers)
  }

  return (
    <form
      className={s.EnterpriseFormRenderer}
      onSubmit={e => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <Box className={s.EnterpriseFormRenderer__contentProgress}>
        <LinearProgressBar value={(step / stepsCount) * 100} />
      </Box>
      <div className={s.EnterpriseFormRenderer__title}>{screen.title}</div>
      <div className={s.EnterpriseFormRenderer__description}>{screen.description}</div>
      <Box direction="vertical" className={s.EnterpriseFormRenderer__fields}>
        {isCheckboxes && (
          <Box className={s.EnterpriseFormRenderer__checkboxgroup}>
            {fields.map(f => (
              <Checkbox
                key={f.key}
                selectionArea="always"
                selectionAreaSkin="outlined"
                selectionAreaPadding="10px 16px"
                checked={!!answers[f.key]}
                onChange={() => {
                  setGlobalError('')
                  setAnswers((old: any) => ({ ...old, [f.key]: !old[f.key] }))
                }}
              >
                {f.label}
              </Checkbox>
            ))}
          </Box>
        )}
        {isRadiogroup && (
          <RadioGroup
            value={selectedRadiogroupValue || undefined}
            selectionArea="always"
            selectionAreaSkin="outlined"
            selectionAreaPadding="10px 16px"
            className={s.EnterpriseFormRenderer__radiogroup}
            onChange={onRadioClick}
          >
            {fields.map(f => (
              <RadioGroup.Radio key={f.key} className={s.EnterpriseFormRenderer__radiobutton} value={f.key}>
                {f.label}
              </RadioGroup.Radio>
            ))}
          </RadioGroup>
        )}
        {!isCheckboxes && !isRadiogroup && (
          <>
            {fields.map(i => (
              <Fragment key={i.key}>
                <div style={{ marginBottom: 18 }}>
                  {['input', 'email', 'url'].includes(i.type) && (
                    <FormField
                      label={i.label}
                      status={(validations[i.key] && 'error') || undefined}
                      statusMessage={validations[i.key]}
                    >
                      <Input
                        name={i.key}
                        placeholder={i.placeholder}
                        onChange={e => onInputChange(i.key, e.target.value)}
                        defaultValue={answers[i.key]}
                      />
                    </FormField>
                  )}
                  {i.type === 'phone' && (
                    <PhoneNumberControl
                      name={i.key}
                      defaultValue={answers[i.key] as [CountryCode, string]}
                      onChange={(answer: [CountryCode, string]) =>
                        setAnswers((old: any) => ({ ...answers, [i.key]: answer }))
                      }
                      statusMessage={validations[i.key]}
                    />
                  )}
                  {i.type === 'textarea' && (
                    <FormField
                      label={i.label}
                      status={(validations[i.key] && 'error') || undefined}
                      statusMessage={validations[i.key]}
                    >
                      <InputArea
                        name={i.key}
                        rows={4}
                        placeholder={i.placeholder}
                        onChange={e => onInputChange(i.key, e.target.value)}
                        defaultValue={answers[i.key]}
                      />
                    </FormField>
                  )}
                </div>
              </Fragment>
            ))}
          </>
        )}
      </Box>
      {screen.agreement && (
        <div className={s.EnterpriseFormRenderer__agreement}>
          <Checkbox
            vAlign="top"
            size="small"
            checked={agreementSelected}
            onChange={e => {
              setGlobalError('')
              setAgreementSelected(current => !current)
            }}
          >
            <span
              dangerouslySetInnerHTML={{
                // inlining the link.
                // <Trans ... /> is not available as all the content comes from wix flows.
                __html:
                  (screen.agreement.match('<1>') &&
                    screen.agreement.match('</1>') &&
                    screen.agreement
                      ?.replace('<1>', `<a href="${PRIVACY_URL}" target="_blank" rel="noopener noreferrer">`)
                      ?.replace('</1>', '</a>')) ||
                  screen.agreement,
              }}
            />
          </Checkbox>
        </div>
      )}
      {(!isRadiogroup || selectedRadiogroupValue) && (
        <Box align="center" direction="vertical">
          <Button type="submit" skin="dark">
            {step === stepsCount - 1 ? content.submit : content.next}
          </Button>
          <Box textAlign="center" minHeight="20px" marginTop="10px">
            {globalError && <FormField status="error" statusMessage={globalError} />}
          </Box>
        </Box>
      )}
    </form>
  )
}
